import React, { useState, useEffect } from 'react';
import { ArrowLeft, Mail, Bell, BellOff, Clock, Globe, Check, AlertCircle, Loader2, Home, Calendar, Sparkles, Trophy, TrendingUp, Heart } from 'lucide-react';
import EgovexLogo from '../components/EgovexLogo';

type LangCode = 'en' | 'it' | 'es' | 'de' | 'fr';

interface EmailPreferencesViewProps {
  token?: string; // If provided, use token-based auth (from email link)
  onBack?: () => void;
  onHome?: () => void;
  lang?: LangCode;
}

interface EmailPreferences {
  emailEnabled: boolean;
  weeklyRecap: boolean;
  insightEmails: boolean;
  celebrationEmails: boolean;
  reengagementEmails: boolean;
  progressEmails: boolean;
  frequencyPreference: 'minimal' | 'normal' | 'all';
  quietHoursStart: string | null;
  quietHoursEnd: string | null;
  timezone: string | null;
}

const TRANSLATIONS: Record<LangCode, {
  title: string;
  subtitle: string;
  allEmails: string;
  allEmailsDesc: string;
  weeklyRecap: string;
  weeklyRecapDesc: string;
  insights: string;
  insightsDesc: string;
  celebrations: string;
  celebrationsDesc: string;
  reengagement: string;
  reengagementDesc: string;
  progress: string;
  progressDesc: string;
  frequency: string;
  frequencyDesc: string;
  minimal: string;
  normal: string;
  all: string;
  quietHours: string;
  quietHoursDesc: string;
  from: string;
  to: string;
  timezone: string;
  save: string;
  saving: string;
  saved: string;
  error: string;
  unsubscribeAll: string;
  unsubscribeAllDesc: string;
  backToApp: string;
  loading: string;
  invalidToken: string;
  preferencesUpdated: string;
  unsubscribed: string;
  resubscribe: string;
  youAreUnsubscribed: string;
}> = {
  en: {
    title: 'Email Preferences',
    subtitle: 'Control how and when we reach out to you',
    allEmails: 'All Emails',
    allEmailsDesc: 'Master switch for all email communications',
    weeklyRecap: 'Weekly Recap',
    weeklyRecapDesc: 'Your weekly summary with insights and progress',
    insights: 'Insights & Patterns',
    insightsDesc: 'When we notice interesting patterns in your journey',
    celebrations: 'Celebrations',
    celebrationsDesc: 'Milestones, streaks, and achievements',
    reengagement: 'Gentle Reminders',
    reengagementDesc: 'Occasional check-ins when we miss you',
    progress: 'Progress Updates',
    progressDesc: 'Goals, decisions, and growth tracking',
    frequency: 'Email Frequency',
    frequencyDesc: 'How often we reach out',
    minimal: 'Minimal (2/week)',
    normal: 'Normal (5/week)',
    all: 'All (10/week)',
    quietHours: 'Quiet Hours',
    quietHoursDesc: 'No emails during these hours',
    from: 'From',
    to: 'To',
    timezone: 'Timezone',
    save: 'Save Preferences',
    saving: 'Saving...',
    saved: 'Saved!',
    error: 'Error saving preferences',
    unsubscribeAll: 'Unsubscribe from all',
    unsubscribeAllDesc: 'Stop all email communications',
    backToApp: 'Back to Egovex',
    loading: 'Loading preferences...',
    invalidToken: 'Invalid or expired link. Please use the link from your email.',
    preferencesUpdated: 'Your preferences have been updated',
    unsubscribed: 'You have been unsubscribed from all emails',
    resubscribe: 'Resubscribe',
    youAreUnsubscribed: 'You are currently unsubscribed from all emails',
  },
  it: {
    title: 'Preferenze Email',
    subtitle: 'Controlla come e quando ti contattiamo',
    allEmails: 'Tutte le Email',
    allEmailsDesc: 'Interruttore principale per tutte le comunicazioni',
    weeklyRecap: 'Recap Settimanale',
    weeklyRecapDesc: 'Il tuo riassunto settimanale con insight e progressi',
    insights: 'Insight e Pattern',
    insightsDesc: 'Quando notiamo pattern interessanti nel tuo percorso',
    celebrations: 'Celebrazioni',
    celebrationsDesc: 'Traguardi, streak e achievement',
    reengagement: 'Promemoria Gentili',
    reengagementDesc: 'Check-in occasionali quando ci manchi',
    progress: 'Aggiornamenti Progresso',
    progressDesc: 'Obiettivi, decisioni e tracciamento crescita',
    frequency: 'Frequenza Email',
    frequencyDesc: 'Quanto spesso ti contattiamo',
    minimal: 'Minima (2/sett)',
    normal: 'Normale (5/sett)',
    all: 'Tutte (10/sett)',
    quietHours: 'Ore di Silenzio',
    quietHoursDesc: 'Nessuna email durante queste ore',
    from: 'Dalle',
    to: 'Alle',
    timezone: 'Fuso orario',
    save: 'Salva Preferenze',
    saving: 'Salvataggio...',
    saved: 'Salvato!',
    error: 'Errore nel salvataggio',
    unsubscribeAll: 'Disiscriviti da tutto',
    unsubscribeAllDesc: 'Ferma tutte le comunicazioni email',
    backToApp: 'Torna a Egovex',
    loading: 'Caricamento preferenze...',
    invalidToken: 'Link non valido o scaduto. Usa il link dalla tua email.',
    preferencesUpdated: 'Le tue preferenze sono state aggiornate',
    unsubscribed: 'Sei stato disiscritto da tutte le email',
    resubscribe: 'Reiscriviti',
    youAreUnsubscribed: 'Sei attualmente disiscritto da tutte le email',
  },
  es: {
    title: 'Preferencias de Email',
    subtitle: 'Controla cmo y cundo te contactamos',
    allEmails: 'Todos los Emails',
    allEmailsDesc: 'Interruptor principal para todas las comunicaciones',
    weeklyRecap: 'Resumen Semanal',
    weeklyRecapDesc: 'Tu resumen semanal con insights y progreso',
    insights: 'Insights y Patrones',
    insightsDesc: 'Cuando notamos patrones interesantes en tu viaje',
    celebrations: 'Celebraciones',
    celebrationsDesc: 'Hitos, rachas y logros',
    reengagement: 'Recordatorios Suaves',
    reengagementDesc: 'Check-ins ocasionales cuando te extraamos',
    progress: 'Actualizaciones de Progreso',
    progressDesc: 'Metas, decisiones y seguimiento de crecimiento',
    frequency: 'Frecuencia de Email',
    frequencyDesc: 'Con qu frecuencia te contactamos',
    minimal: 'Mnima (2/sem)',
    normal: 'Normal (5/sem)',
    all: 'Todas (10/sem)',
    quietHours: 'Horas de Silencio',
    quietHoursDesc: 'Sin emails durante estas horas',
    from: 'Desde',
    to: 'Hasta',
    timezone: 'Zona horaria',
    save: 'Guardar Preferencias',
    saving: 'Guardando...',
    saved: 'Guardado!',
    error: 'Error al guardar',
    unsubscribeAll: 'Darse de baja de todo',
    unsubscribeAllDesc: 'Detener todas las comunicaciones por email',
    backToApp: 'Volver a Egovex',
    loading: 'Cargando preferencias...',
    invalidToken: 'Enlace invlido o expirado. Usa el enlace de tu email.',
    preferencesUpdated: 'Tus preferencias han sido actualizadas',
    unsubscribed: 'Te has dado de baja de todos los emails',
    resubscribe: 'Volver a suscribirse',
    youAreUnsubscribed: 'Actualmente ests dado de baja de todos los emails',
  },
  de: {
    title: 'E-Mail-Einstellungen',
    subtitle: 'Kontrolliere wie und wann wir dich kontaktieren',
    allEmails: 'Alle E-Mails',
    allEmailsDesc: 'Hauptschalter fr alle E-Mail-Kommunikation',
    weeklyRecap: 'Wchentliche Zusammenfassung',
    weeklyRecapDesc: 'Deine wchentliche Zusammenfassung mit Einblicken',
    insights: 'Einblicke & Muster',
    insightsDesc: 'Wenn wir interessante Muster auf deiner Reise bemerken',
    celebrations: 'Feiern',
    celebrationsDesc: 'Meilensteine, Serien und Erfolge',
    reengagement: 'Sanfte Erinnerungen',
    reengagementDesc: 'Gelegentliche Check-ins wenn wir dich vermissen',
    progress: 'Fortschritts-Updates',
    progressDesc: 'Ziele, Entscheidungen und Wachstumsverfolgung',
    frequency: 'E-Mail-Hufigkeit',
    frequencyDesc: 'Wie oft wir dich kontaktieren',
    minimal: 'Minimal (2/Woche)',
    normal: 'Normal (5/Woche)',
    all: 'Alle (10/Woche)',
    quietHours: 'Ruhezeiten',
    quietHoursDesc: 'Keine E-Mails whrend dieser Stunden',
    from: 'Von',
    to: 'Bis',
    timezone: 'Zeitzone',
    save: 'Einstellungen Speichern',
    saving: 'Speichern...',
    saved: 'Gespeichert!',
    error: 'Fehler beim Speichern',
    unsubscribeAll: 'Von allem abmelden',
    unsubscribeAllDesc: 'Alle E-Mail-Kommunikation stoppen',
    backToApp: 'Zurck zu Egovex',
    loading: 'Lade Einstellungen...',
    invalidToken: 'Ungltiger oder abgelaufener Link.',
    preferencesUpdated: 'Deine Einstellungen wurden aktualisiert',
    unsubscribed: 'Du wurdest von allen E-Mails abgemeldet',
    resubscribe: 'Wieder anmelden',
    youAreUnsubscribed: 'Du bist derzeit von allen E-Mails abgemeldet',
  },
  fr: {
    title: 'Prfrences Email',
    subtitle: 'Contrle comment et quand nous te contactons',
    allEmails: 'Tous les Emails',
    allEmailsDesc: 'Interrupteur principal pour toutes les communications',
    weeklyRecap: 'Rsum Hebdomadaire',
    weeklyRecapDesc: 'Ton rsum hebdomadaire avec insights et progrs',
    insights: 'Insights & Patterns',
    insightsDesc: 'Quand nous remarquons des patterns intressants',
    celebrations: 'Clbrations',
    celebrationsDesc: 'Jalons, sries et accomplissements',
    reengagement: 'Rappels Doux',
    reengagementDesc: 'Check-ins occasionnels quand tu nous manques',
    progress: 'Mises  Jour Progrs',
    progressDesc: 'Objectifs, dcisions et suivi de croissance',
    frequency: 'Frquence des Emails',
    frequencyDesc: ' quelle frquence nous te contactons',
    minimal: 'Minimale (2/sem)',
    normal: 'Normale (5/sem)',
    all: 'Toutes (10/sem)',
    quietHours: 'Heures de Silence',
    quietHoursDesc: 'Pas demails pendant ces heures',
    from: 'De',
    to: '',
    timezone: 'Fuseau horaire',
    save: 'Enregistrer Prfrences',
    saving: 'Enregistrement...',
    saved: 'Enregistr!',
    error: 'Erreur lors de lenregistrement',
    unsubscribeAll: 'Se dsabonner de tout',
    unsubscribeAllDesc: 'Arrter toutes les communications par email',
    backToApp: 'Retour  Egovex',
    loading: 'Chargement des prfrences...',
    invalidToken: 'Lien invalide ou expir.',
    preferencesUpdated: 'Tes prfrences ont t mises  jour',
    unsubscribed: 'Tu tes dsabonn de tous les emails',
    resubscribe: 'Se rabonner',
    youAreUnsubscribed: 'Tu es actuellement dsabonn de tous les emails',
  },
};

// Toggle switch component
const Toggle: React.FC<{
  enabled: boolean;
  onChange: (enabled: boolean) => void;
  disabled?: boolean;
}> = ({ enabled, onChange, disabled }) => (
  <button
    type="button"
    onClick={() => !disabled && onChange(!enabled)}
    disabled={disabled}
    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
      enabled ? 'bg-indigo-600' : 'bg-gray-200'
    } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
  >
    <span
      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
        enabled ? 'translate-x-5' : 'translate-x-0'
      }`}
    />
  </button>
);

// Preference row component
const PreferenceRow: React.FC<{
  icon: React.ReactNode;
  title: string;
  description: string;
  enabled: boolean;
  onChange: (enabled: boolean) => void;
  disabled?: boolean;
  isMain?: boolean;
}> = ({ icon, title, description, enabled, onChange, disabled, isMain }) => (
  <div className={`flex items-center justify-between p-4 ${isMain ? 'bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl' : 'border-b border-gray-100 last:border-b-0'}`}>
    <div className="flex items-start gap-3 flex-1">
      <div className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center ${isMain ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600'}`}>
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <h3 className={`font-semibold ${isMain ? 'text-indigo-900' : 'text-gray-900'}`}>{title}</h3>
        <p className="text-sm text-gray-500 mt-0.5">{description}</p>
      </div>
    </div>
    <Toggle enabled={enabled} onChange={onChange} disabled={disabled} />
  </div>
);

const EmailPreferencesView: React.FC<EmailPreferencesViewProps> = ({
  token,
  onBack,
  onHome,
  lang = 'en',
}) => {
  const t = TRANSLATIONS[lang] || TRANSLATIONS.en;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invalidToken, setInvalidToken] = useState(false);

  const [preferences, setPreferences] = useState<EmailPreferences>({
    emailEnabled: true,
    weeklyRecap: true,
    insightEmails: true,
    celebrationEmails: true,
    reengagementEmails: true,
    progressEmails: true,
    frequencyPreference: 'normal',
    quietHoursStart: '22:00',
    quietHoursEnd: '08:00',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });

  // Fetch preferences on mount
  useEffect(() => {
    const fetchPreferences = async () => {
      try {
        const url = token
          ? `/api/email/preferences/${token}`
          : '/api/email/preferences';

        const response = await fetch(url, { credentials: 'include' });

        if (!response.ok) {
          if (token && response.status === 404) {
            setInvalidToken(true);
          }
          throw new Error('Failed to fetch preferences');
        }

        const data = await response.json();

        // Handle the HTML response from token-based endpoint
        if (typeof data === 'string') {
          // Token endpoint returned HTML, we need to parse preferences from a different response
          setInvalidToken(true);
          return;
        }

        setPreferences({
          emailEnabled: data.emailEnabled ?? true,
          weeklyRecap: data.weeklyRecap ?? true,
          insightEmails: data.insightEmails ?? true,
          celebrationEmails: data.celebrationEmails ?? true,
          reengagementEmails: data.reengagementEmails ?? true,
          progressEmails: data.progressEmails ?? true,
          frequencyPreference: data.frequencyPreference || 'normal',
          quietHoursStart: data.quietHoursStart || '22:00',
          quietHoursEnd: data.quietHoursEnd || '08:00',
          timezone: data.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
        });
      } catch (err) {
        console.error('Error fetching preferences:', err);
        // Don't show error for new users without preferences
      } finally {
        setLoading(false);
      }
    };

    fetchPreferences();
  }, [token]);

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    setError(null);

    try {
      const url = token
        ? `/api/email/preferences/${token}`
        : '/api/email/preferences';

      const response = await fetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(preferences),
      });

      if (!response.ok) {
        throw new Error('Failed to save preferences');
      }

      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(t.error);
    } finally {
      setSaving(false);
    }
  };

  const handleUnsubscribeAll = async () => {
    setPreferences(prev => ({ ...prev, emailEnabled: false }));

    try {
      const url = token
        ? `/api/email/preferences/${token}`
        : '/api/email/preferences';

      await fetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ emailEnabled: false }),
      });

      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(t.error);
    }
  };

  const handleResubscribe = async () => {
    setPreferences(prev => ({
      ...prev,
      emailEnabled: true,
      weeklyRecap: true,
      insightEmails: true,
      celebrationEmails: true,
      reengagementEmails: true,
      progressEmails: true,
    }));

    try {
      const url = token
        ? `/api/email/preferences/${token}`
        : '/api/email/preferences';

      await fetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          emailEnabled: true,
          weeklyRecap: true,
          insightEmails: true,
          celebrationEmails: true,
          reengagementEmails: true,
          progressEmails: true,
        }),
      });

      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(t.error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
          <p className="text-gray-600">{t.loading}</p>
        </div>
      </div>
    );
  }

  if (invalidToken) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">{t.invalidToken}</h2>
          <a
            href="/"
            className="mt-6 inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-xl font-semibold hover:bg-indigo-700 transition-colors"
          >
            <Home className="w-5 h-5" />
            {t.backToApp}
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {onBack && (
              <button
                onClick={onBack}
                className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
            )}
            <EgovexLogo size="sm" />
          </div>
          <a
            href="/"
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors text-sm font-medium text-gray-700"
          >
            <Home className="w-4 h-4" />
            {t.backToApp}
          </a>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8">
        {/* Title */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-indigo-500/30">
            <Mail className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">{t.title}</h1>
          <p className="text-gray-500 mt-1">{t.subtitle}</p>
        </div>

        {/* Unsubscribed state */}
        {!preferences.emailEnabled && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6 text-center">
            <BellOff className="w-10 h-10 text-amber-600 mx-auto mb-3" />
            <h3 className="font-semibold text-amber-900 mb-2">{t.youAreUnsubscribed}</h3>
            <button
              onClick={handleResubscribe}
              className="mt-3 px-6 py-2 bg-amber-600 text-white rounded-xl font-medium hover:bg-amber-700 transition-colors"
            >
              {t.resubscribe}
            </button>
          </div>
        )}

        {/* Main toggle */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mb-6">
          <PreferenceRow
            icon={<Bell className="w-5 h-5" />}
            title={t.allEmails}
            description={t.allEmailsDesc}
            enabled={preferences.emailEnabled}
            onChange={(enabled) => setPreferences(prev => ({ ...prev, emailEnabled: enabled }))}
            isMain
          />
        </div>

        {/* Category toggles */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mb-6">
          <PreferenceRow
            icon={<Calendar className="w-5 h-5" />}
            title={t.weeklyRecap}
            description={t.weeklyRecapDesc}
            enabled={preferences.weeklyRecap}
            onChange={(enabled) => setPreferences(prev => ({ ...prev, weeklyRecap: enabled }))}
            disabled={!preferences.emailEnabled}
          />
          <PreferenceRow
            icon={<Sparkles className="w-5 h-5" />}
            title={t.insights}
            description={t.insightsDesc}
            enabled={preferences.insightEmails}
            onChange={(enabled) => setPreferences(prev => ({ ...prev, insightEmails: enabled }))}
            disabled={!preferences.emailEnabled}
          />
          <PreferenceRow
            icon={<Trophy className="w-5 h-5" />}
            title={t.celebrations}
            description={t.celebrationsDesc}
            enabled={preferences.celebrationEmails}
            onChange={(enabled) => setPreferences(prev => ({ ...prev, celebrationEmails: enabled }))}
            disabled={!preferences.emailEnabled}
          />
          <PreferenceRow
            icon={<Heart className="w-5 h-5" />}
            title={t.reengagement}
            description={t.reengagementDesc}
            enabled={preferences.reengagementEmails}
            onChange={(enabled) => setPreferences(prev => ({ ...prev, reengagementEmails: enabled }))}
            disabled={!preferences.emailEnabled}
          />
          <PreferenceRow
            icon={<TrendingUp className="w-5 h-5" />}
            title={t.progress}
            description={t.progressDesc}
            enabled={preferences.progressEmails}
            onChange={(enabled) => setPreferences(prev => ({ ...prev, progressEmails: enabled }))}
            disabled={!preferences.emailEnabled}
          />
        </div>

        {/* Frequency selection */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-6">
          <h3 className="font-semibold text-gray-900 mb-1">{t.frequency}</h3>
          <p className="text-sm text-gray-500 mb-4">{t.frequencyDesc}</p>
          <div className="flex gap-2">
            {(['minimal', 'normal', 'all'] as const).map((freq) => (
              <button
                key={freq}
                onClick={() => setPreferences(prev => ({ ...prev, frequencyPreference: freq }))}
                disabled={!preferences.emailEnabled}
                className={`flex-1 py-3 px-4 rounded-xl font-medium transition-all ${
                  preferences.frequencyPreference === freq
                    ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-500/30'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                } ${!preferences.emailEnabled ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {t[freq]}
              </button>
            ))}
          </div>
        </div>

        {/* Quiet hours */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center">
              <Clock className="w-5 h-5 text-gray-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">{t.quietHours}</h3>
              <p className="text-sm text-gray-500">{t.quietHoursDesc}</p>
            </div>
          </div>
          <div className="flex gap-4">
            <div className="flex-1">
              <label className="block text-sm text-gray-600 mb-1">{t.from}</label>
              <input
                type="time"
                value={preferences.quietHoursStart || '22:00'}
                onChange={(e) => setPreferences(prev => ({ ...prev, quietHoursStart: e.target.value }))}
                disabled={!preferences.emailEnabled}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-50"
              />
            </div>
            <div className="flex-1">
              <label className="block text-sm text-gray-600 mb-1">{t.to}</label>
              <input
                type="time"
                value={preferences.quietHoursEnd || '08:00'}
                onChange={(e) => setPreferences(prev => ({ ...prev, quietHoursEnd: e.target.value }))}
                disabled={!preferences.emailEnabled}
                className="w-full px-3 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-50"
              />
            </div>
          </div>
        </div>

        {/* Save button */}
        <button
          onClick={handleSave}
          disabled={saving}
          className={`w-full py-4 rounded-2xl font-semibold text-lg transition-all flex items-center justify-center gap-2 ${
            saved
              ? 'bg-green-500 text-white'
              : 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:from-indigo-700 hover:to-purple-700 shadow-lg shadow-indigo-500/30'
          } ${saving ? 'opacity-75 cursor-wait' : ''}`}
        >
          {saving ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              {t.saving}
            </>
          ) : saved ? (
            <>
              <Check className="w-5 h-5" />
              {t.saved}
            </>
          ) : (
            t.save
          )}
        </button>

        {error && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-center">
            {error}
          </div>
        )}

        {/* Unsubscribe all */}
        {preferences.emailEnabled && (
          <div className="mt-8 pt-6 border-t border-gray-200">
            <button
              onClick={handleUnsubscribeAll}
              className="w-full py-3 text-gray-500 hover:text-red-600 transition-colors text-sm"
            >
              {t.unsubscribeAll}
            </button>
          </div>
        )}
      </main>
    </div>
  );
};

export default EmailPreferencesView;
